import { action, computed, observable, makeAutoObservable } from 'mobx';
import { arrayMove } from 'react-movable';
import { AppState } from 'src/AppState/AppState';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { CompetitionsType } from 'src/AppState/sdk/staff';
import { CompetitionId } from 'src_common/common/websocket2/id/WebsocketId';
import { Value } from 'src_common/common/mobx-utils/Value';
import { GradeType } from 'src/domains/sportsbook/trading/sdk/models/grades/gradeModels';

export type CompetitionType = CompetitionsType[number];

interface SmallCompetitionType {
    id: number;
    displayOrder: number;
}

const sortCompetitions = (competitions: Array<SmallCompetitionType>): void => {
    competitions.sort((competitionA, competitionB) => {
        return competitionA.displayOrder === competitionB.displayOrder
            ? 0
            : competitionA.displayOrder > competitionB.displayOrder
              ? -1
              : 1;
    });
};

const getSortedCompetitionIds = (
    competitions: Array<CompetitionType>,
    displayOrderChanges: Map<number, number>
): Array<SmallCompetitionType> => {
    const list = competitions.map((item) => {
        const id = item.id;
        const displayOrder = displayOrderChanges.get(id) ?? item.displayOrder;

        return {
            id,
            displayOrder,
        };
    });

    sortCompetitions(list);

    return list;
};

class OrderedCompetitionsState {
    private readonly firstList: Resource<Array<CompetitionType>>;

    private displayOrderChanges: Value<Map<number, number>>;

    public constructor(appState: AppState, sportId: string) {
        this.firstList = new Resource<Array<CompetitionType>>(async () => {
            return await appState.sdkStaff.getCompetitionsBySportId(sportId);
        });

        this.displayOrderChanges = new Value(new Map());
        makeAutoObservable(this);
    }

    //Get clone of displayOrderChanges
    public getDisplayOrderChanges(): Map<number, number> {
        const displayOrderChanges = this.displayOrderChanges.getValue();
        const newDisplayOrderChanges = new Map();

        for (const [key, value] of displayOrderChanges) {
            newDisplayOrderChanges.set(key, value);
        }

        return newDisplayOrderChanges;
    }

    public setDisplayOrderChanges(displayOrderChanges: Map<number, number>): void {
        this.displayOrderChanges.setValue(displayOrderChanges);
    }

    private getCurrentDisplayOrder(id: number): number | null {
        for (const item of this.firstList.getReady() ?? []) {
            if (item.id === id) {
                return item.displayOrder;
            }
        }

        return null;
    }

    public setDisplayOrder(displayOrderChanges: Map<number, number>, id: number, newDisplayOrder: number): boolean {
        const currentDisplayOrder = displayOrderChanges.get(id) ?? this.getCurrentDisplayOrder(id) ?? -1;

        if (currentDisplayOrder !== newDisplayOrder) {
            displayOrderChanges.set(id, newDisplayOrder);
            return true;
        }

        return false;
    }

    @computed public get competitionsList(): Array<CompetitionType> {
        const first = this.firstList.getReady();
        if (first === null) {
            return [];
        }

        const displayOrderChanges = this.displayOrderChanges.getValue();
        const sortedIds = getSortedCompetitionIds(first, displayOrderChanges);

        const result = [];

        for (const competition of sortedIds) {
            const model = this.getCompetitionModelByIdOld(competition.id);
            if (model !== null) {
                result.push(model);
            }
        }

        return result;
    }

    public async refresh(): Promise<void> {
        await this.firstList.refresh();
    }

    private getCompetitionModelByIdOld(competitionId: number): CompetitionType | null {
        for (const competition of this.firstList.getReady() ?? []) {
            if (competition.id === competitionId) {
                return competition;
            }
        }

        return null;
    }

    @computed public get isLoading(): boolean {
        const loading = this.firstList.get().type === 'loading';

        return loading;
    }
}

export class SportCompetitionsState {
    public readonly sportGrades: Resource<Array<GradeType>>;

    @observable public openedCompetition: CompetitionId | null = null;

    private orderedCompetitions: OrderedCompetitionsState;
    @observable private movedIdValue: number | null = null;

    public constructor(
        private readonly appState: AppState,
        private readonly sportId: string
    ) {
        this.sportGrades = new Resource(async () => {
            return await this.appState.sdkStaff.trading.getSportsGrades(sportId);
        });
        this.orderedCompetitions = new OrderedCompetitionsState(appState, sportId);
        makeAutoObservable(this);
    }

    @computed public get grades(): Array<GradeType> {
        const gradesResponse = this.sportGrades.get();
        if (gradesResponse.type === 'ready') {
            return gradesResponse.value;
        }

        return [];
    }

    public onGradeChange = async (type: string, value: string, competitionId: CompetitionId): Promise<void> => {
        // eslint-disable-next-line no-restricted-globals
        const grade = this.grades.find((grade) => grade.id === Number(value));
        const idV2 = await competitionId.idv2();
        await this.appState.sdkStaff.patchCompetition({
            sport_id: this.sportId,
            competition_id: competitionId.toOldId(),
            requestBody: {
                idV2,
                grades: {
                    [type]: {
                        id: grade?.id,
                        name: grade?.name,
                    },
                },
            },
        });
    };

    @computed public get movedId(): CompetitionId | null {
        if (this.movedIdValue === null) {
            return null;
        }

        return this.appState.sdkStaff.models.id.getCompetitionId(this.movedIdValue);
    }

    @computed public get competitionsList(): Array<CompetitionType> {
        return this.orderedCompetitions.competitionsList;
    }

    @computed public get isLoading(): boolean {
        return this.orderedCompetitions.isLoading;
    }

    private getMovedItemId(oldIndex: number): number | null {
        const list = this.competitionsList;

        const item = list[oldIndex];
        if (item === undefined) {
            return null;
        }
        return item.id;
    }

    public getCompetitionToMoveIndex(competitionId: CompetitionId): number {
        return this.competitionsList.findIndex(
            (item) => this.appState.sdkStaff.models.id.getCompetitionId(item.id) === competitionId
        );
    }
    @action public onMoveCompetitionToTheTopHandler = async (
        e: React.MouseEvent,
        competitionId: CompetitionId
    ): Promise<void> => {
        e.preventDefault();
        e.stopPropagation();
        const competitionToMoveIndex = this.getCompetitionToMoveIndex(competitionId);
        if (competitionToMoveIndex === -1) {
            console.error('onMoveCompetitionToTheTopHandler - competition index not found ');
            return;
        }
        await this.onChangeDisplayOrder({ oldIndex: competitionToMoveIndex, newIndex: 0 });
    };
    @action public onChangeDisplayOrder = async ({
        oldIndex,
        newIndex,
    }: {
        oldIndex: number;
        newIndex: number;
    }): Promise<void> => {
        if (this.movedIdValue !== null) {
            console.error('onChangeDisplayOrder - ignore');
            return;
        }

        const movedId = this.getMovedItemId(oldIndex);
        if (movedId === null) {
            console.error('onChangeDisplayOrder - incorrect index of the item to be moved');
            return;
        }

        this.movedIdValue = movedId;

        const list = arrayMove(this.competitionsList, oldIndex, newIndex);

        const displayOrderChanges: Map<number, number> = this.orderedCompetitions.getDisplayOrderChanges();

        const updateList: Record<
            string,
            {
                idV2?: null | undefined | string;
                patch: {
                    displayOrder: number;
                };
            }
        > = {};

        for (const [idx, item] of list.entries()) {
            const newDisplayOrder = 100000 - idx;
            const shouldSave = this.orderedCompetitions.setDisplayOrder(displayOrderChanges, item.id, newDisplayOrder);

            // We need this condition to send a smaller body, if we send too big, the old trading will explode - DO NOT REMOVE
            if (shouldSave) {
                updateList[item.id] = {
                    patch: {
                        displayOrder: newDisplayOrder,
                    },
                    idV2: item.idV2,
                };
            }
        }

        this.orderedCompetitions.setDisplayOrderChanges(displayOrderChanges);

        try {
            await this.appState.sdkStaff.patchCompetitionsReorder({
                requestBody: {
                    competitions: updateList,
                    sport: this.sportId,
                },
            });
        } catch (e) {
            this.appState.reduxAction.addError('operation timed out');
        }

        this.movedIdValue = null;
    };

    @action public onClickCompetition = (competitionId: CompetitionId): void => {
        this.openedCompetition = competitionId;
    };

    public refreshCompetitions = async (): Promise<void> => {
        await this.orderedCompetitions.refresh();
    };
}
